import { ChipSelect } from '@/dsm-components';
import { useGetAvlItemsQuery } from '@/services/approvedVendorList';
import { useCallback, useMemo } from 'react';
import { UseFormSetValue } from 'react-hook-form';

import { VendorEquipmentTypes } from 'types';

import { DialogFormValues } from './checklistItemInputOptions';
import { serializeParamsRaw } from './paramsRaw';

// High enough that any equipment type's full manufacturer set fits in one page; matches the
// precedent in ApprovedVendorItemForm's manufacturer-suggestions query.
const MANUFACTURER_FETCH_LIMIT = 1000;

interface AVLManufacturerParamAdderProps {
  fieldPrefix: `conditions` | `inputs.${number}.conditions`;
  equipmentType: VendorEquipmentTypes;
  currentParams: string[];
  conditionIndex: number;
  setValue: UseFormSetValue<DialogFormValues>;
}

export const AVLManufacturerParamAdder = ({
  fieldPrefix,
  equipmentType,
  currentParams,
  conditionIndex,
  setValue,
}: AVLManufacturerParamAdderProps) => {
  // No `programType` — this deliberately queries across every program type, not just one.
  const { data: response, isLoading } = useGetAvlItemsQuery({
    equipmentType,
    limit: MANUFACTURER_FETCH_LIMIT,
  });

  const availableOptions = useMemo(() => {
    if (!response) return [];
    if (response.total > response.data.length) {
      console.warn(
        `AVLManufacturerParamAdder: fetched ${response.data.length} of ${response.total} AVL items for equipmentType "${equipmentType}" — some manufacturers may be missing from the list.`,
      );
    }
    const vendors = response.data ?? [];
    const manufacturers = [...new Set(vendors.map((v) => v.manufacturer))].toSorted();
    return manufacturers.filter((m) => !currentParams.includes(m)).map((m) => ({ value: m, label: m }));
  }, [response, currentParams, equipmentType]);

  const handleChange = useCallback(
    (newValue: string) => {
      if (newValue && !currentParams.includes(newValue)) {
        const newParams = [...currentParams, newValue];
        setValue(`${fieldPrefix}.${conditionIndex}.paramsRaw`, serializeParamsRaw(newParams));
      }
    },
    [fieldPrefix, currentParams, conditionIndex, setValue],
  );

  if (isLoading) {
    return <span className="text-xs text-text-tertiary">Loading...</span>;
  }

  if (availableOptions.length === 0) {
    return null;
  }

  return <ChipSelect options={availableOptions} value="" onChange={handleChange} placeholder="+ manufacturer" />;
};
